import { Box, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import html2pdf from "html2pdf.js";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import useAPI from "../../../../../api/hooks/useAPI";
import useColors from "../../../../../api/hooks/useColors";
import { dashboardKeys } from "../../../../../api/queryKeys";
import LoadingIcon from "../../../../../components/LoadingIcon";
import {
  FILE_EXTENSIONS,
  generateFileName,
} from "../../../../../hooks/useFileNaming";
import {
  ComplexEntityTypes,
  LATEST,
  TimePoint,
} from "../../../../../utils/constants";
import DonutPieChart from "../../../../dashboard/mssp_widgets/assets_breakdown/DonutPieChart";
import { EntityTypeInfo } from "../../../../stack/environment/components/constants";
import { icons } from "../../../../stack/environment/icons/iconExport";
import {
  baseTimePointOptions,
  calculateTrendData,
  createPdfConfig,
  createPdfContainer,
  createPdfHeader,
  createTrendSortMenuItems,
  enhanceAssetCardShadows,
  measureContentForPdf,
} from "../../utils/commonWidgetUtils";
import WidgetWrapper from "../../WidgetWrapper";
import EntityCard from "./EntityCard";
const InventoryWidget = forwardRef(({ mainTimePoint }, ref) => {
  const colors = useColors();
  const { getInventoryAssetsTrend, trackEvent } = useAPI();
  const [baseTimePoint, setBaseTimePoint] = useState(TimePoint.LATEST);
  const widgetContentRef = useRef(null);

  const {
    data: inventoryAssetsTrend,
    isLoading: isLoadingInventoryAssetsTrend,
  } = useQuery({
    queryKey: dashboardKeys.widgetType("inventoryAssetsTrend"),
    queryFn: getInventoryAssetsTrend,
    select: (data) => data.data,
  });

  useEffect(() => {
    if (inventoryAssetsTrend && inventoryAssetsTrend[mainTimePoint]) {
      setBaseTimePoint(mainTimePoint);
    }
  }, [mainTimePoint]);

  const currentTimePoint = useMemo(() => {
    if (!inventoryAssetsTrend || Object.keys(inventoryAssetsTrend).length === 0)
      return {};
    return inventoryAssetsTrend[LATEST];
  }, [inventoryAssetsTrend]);

  // Calculate trend data for entities and coverage
  const trendData = useMemo(() => {
    if (!inventoryAssetsTrend || !currentTimePoint) return {};
    const baseData = inventoryAssetsTrend[baseTimePoint];
    return calculateTrendData(currentTimePoint, baseData);
  }, [currentTimePoint, inventoryAssetsTrend, baseTimePoint]);

  const colorsByEntityType = {
    [ComplexEntityTypes.USER]: colors.secondary_600,
    [ComplexEntityTypes.HOST]: colors.secondary_500,
    [ComplexEntityTypes.DATA_STORE]: colors.secondary_400,
    [ComplexEntityTypes.SERVER]: colors.secondary_300,
    [ComplexEntityTypes.NONHUMAN_IDENTITY]: colors.secondary_200,
    [ComplexEntityTypes.APP]: colors.secondary_100,
  };

  const assetsPieChartDataToDisplay =
    (currentTimePoint &&
      Object.entries(currentTimePoint)?.map(([key, value], index) => ({
        id: index,
        name: key,
        value: value.entities,
        coverage: value.coverage,
        label: EntityTypeInfo[key]?.title + "s",
        color: colorsByEntityType[key],
        trend: trendData[key] || null,
      }))) ||
    [];

  const sortMenuItems = createTrendSortMenuItems(
    baseTimePoint,
    setBaseTimePoint,
    inventoryAssetsTrend,
    icons,
    trackEvent,
    "inventory-assets"
  );

  const handleExportToPdf = useCallback(
    async (isCombinedExport = false) => {
      if (!widgetContentRef.current || isLoadingInventoryAssetsTrend)
        return null;

      try {
        if (isCombinedExport) {
          // For combined export, return just the widget content with header
          const widgetContainer = document.createElement("div");

          // Add widget header
          const widgetHeader = createPdfHeader(
            "Inventory Report",
            baseTimePoint !== LATEST
              ? `Current count vs. ${baseTimePointOptions[baseTimePoint]}`
              : ""
          );
          widgetContainer.appendChild(widgetHeader);

          // Clone and style widget content
          const widgetClone = widgetContentRef.current.cloneNode(true);
          widgetClone.style.cssText = `
          background-color: white;
          padding: 24px;
          border: none;
          width: 100%;
          min-height: fit-content;
        `;

          // Enhance shadows for asset cards
          enhanceAssetCardShadows(widgetClone);
          widgetContainer.appendChild(widgetClone);

          return widgetContainer;
        } else {
          // Individual export - original functionality
          const entirePageElement = await createPdfContainer();
          const subtitle =
            baseTimePoint !== LATEST
              ? `Current count vs. ${baseTimePointOptions[baseTimePoint]}`
              : "";
          const header = createPdfHeader("Inventory Report", subtitle);
          entirePageElement.appendChild(header);

          // Clone and style widget content
          const widgetClone = widgetContentRef.current.cloneNode(true);
          widgetClone.style.cssText = `
          background-color: white;
          padding: 24px;
          border: none;
          width: 100%;
          min-height: fit-content;
        `;

          // Enhance shadows for asset cards
          enhanceAssetCardShadows(widgetClone);
          entirePageElement.appendChild(widgetClone);

          // Measure content dimensions
          const { contentWidth, contentHeight } = await measureContentForPdf(
            entirePageElement
          );

          // Generate PDF
          const pdfConfig = createPdfConfig(
            generateFileName("Breeze_Inventory", null, FILE_EXTENSIONS.PDF),
            contentWidth,
            contentHeight
          );

          await html2pdf().set(pdfConfig).from(entirePageElement).save();
        }
      } catch (error) {
        console.error("Failed to export PDF:", error);
        return null;
      }
    },
    [widgetContentRef, isLoadingInventoryAssetsTrend]
  );

  // Expose the export function to parent components
  useImperativeHandle(
    ref,
    () => ({
      exportToPdf: handleExportToPdf,
      widgetId: "inventory",
      hasExportCapability: true,
    }),
    [handleExportToPdf]
  );
  return (
    <WidgetWrapper
      title={"Inventory"}
      sortMenuItems={{
        data: sortMenuItems,
        tooltip: "View changes since selected time",
      }}
      info="Shows entity count and coverage across your environment."
      viewAll={{
        handleViewAll: () => {
          window.open("/inventory/environment", "_blank");
        },
        tooltip: "View all inventory data",
        newTab: true,
      }}
      handleExportToPdf={() => handleExportToPdf(false)}
    >
      {isLoadingInventoryAssetsTrend ? (
        <Box>
          <LoadingIcon size={24} />
        </Box>
      ) : !currentTimePoint || Object.keys(currentTimePoint).length === 0 ? (
        <Box>
          <Typography variant="caption">No data available</Typography>
        </Box>
      ) : (
        <Box ref={widgetContentRef} sx={{ padding: "0px 16px 0px 16px" }}>
          <Typography variant="caption" marginLeft={2.5}>
            Current count{" "}
            {baseTimePoint !== LATEST
              ? `vs. ${baseTimePointOptions[baseTimePoint]}`
              : ""}
          </Typography>
          <Box
            display="flex"
            padding={2}
            flexDirection="row"
            gap={2}
            alignItems="center"
          >
            <Box
              display="flex"
              flexDirection="row"
              gap={2}
              flexWrap="wrap"
              width={"70%"}
            >
              {assetsPieChartDataToDisplay.map((item) => (
                <EntityCard
                  key={item.id}
                  name={item.name}
                  value={item.value}
                  coverage={item.coverage}
                  trend={item.trend}
                  baseTimePoint={baseTimePoint}
                />
              ))}
            </Box>

            <Box
              width={"30%"}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
              }}
            >
              <DonutPieChart data={assetsPieChartDataToDisplay} />
            </Box>
          </Box>
        </Box>
      )}
    </WidgetWrapper>
  );
});

InventoryWidget.displayName = "InventoryWidget";

export default InventoryWidget;
